import React, { useState, useEffect, useRef } from "react"

type Option = {
  value: string
  label: string
}

type MultiSelectDropdownProps = {
  id: string
  titleText: string
  options: Option[]
  setSelectedValues: (data: { value: string; label: string }[]) => void
  defaultSelectedOptions?: Option[]
  disable?: boolean
}

const MultipleSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  id,
  titleText,
  options,
  setSelectedValues,
  defaultSelectedOptions = [],
  disable = false,
}) => {
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([])
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (
      JSON.stringify(defaultSelectedOptions) !== JSON.stringify(selectedOptions)
    ) {
      setSelectedOptions(defaultSelectedOptions || [])
    }
  }, [defaultSelectedOptions])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target
    const selectedOption = options.find((option) => option.value === value)

    if (!selectedOption) return

    setSelectedOptions((prev) => {
      const updatedOptions = checked
        ? [...prev, selectedOption]
        : prev.filter((option) => option.value !== value)

      setTimeout(() => {
        setSelectedValues(updatedOptions)
      }, 0)

      return updatedOptions
    })
  }

  const handleDropdownToggle = () => {
    setDropdownOpen(!dropdownOpen)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  return (
    <div className="self-stretch items-start w-full justify-start gap-[6px] text-left text-sm text-gray-700 font-text-md-regular1">
      <div className="self-stretch w-full flex flex-col items-start justify-start gap-[6px]">
        <label
          htmlFor={id}
          className="relative w-full leading-[24px] font-medium">
          {titleText}
          <div
            className="form-control relative w-full"
            data-testid={id}
            ref={dropdownRef}>
            <div
              className="select select-bordered cursor-pointer appearance-none self-stretch w-full rounded-lg bg-base-white shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] overflow-hidden flex flex-row px-3.5 items-center justify-start gap-[8px] text-base text-gray-500 border-[1px] border-solid border-gray-300"
              style={{
                width: "initial",
                pointerEvents: disable ? "none" : "all",
                opacity: disable ? "0.5" : "1",
              }}
              onClick={handleDropdownToggle}>
              <span>
                {selectedOptions.length > 0
                  ? selectedOptions.map((option) => option.label).join(", ")
                  : "Select group name"}
              </span>
            </div>

            {dropdownOpen && (
              <div className="self-stretch absolute top-full left-0 w-full z-10 bg-white border border-solid border-slategray-100 shadow-lg h-32 overflow-auto max-h-fit">
                {options.map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center py-2 px-4 cursor-pointer hover:bg-blue-50 text-gray-700"
                    onMouseDown={(e) => e.preventDefault()}>
                    <input
                      type="checkbox"
                      value={option.value}
                      checked={selectedOptions.some(
                        (selected) => selected.value === option.value,
                      )}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            )}
          </div>
        </label>
      </div>
    </div>
  )
}

export default MultipleSelectDropdown
